import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import axios from 'axios';
import { Bot, ArrowRight, ArrowLeft, Check, Loader2, Zap, Target, Shield, TrendingUp, Play, Pause, Settings } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const GOALS = [
  { id:'maximize_roas', name:'Maximize ROAS', desc:'Optimize bids and budgets to get the highest return on ad spend.', icon:TrendingUp, color:'#06D6A0' },
  { id:'reduce_cac', name:'Reduce CAC', desc:'Lower customer acquisition cost by finding efficient channels and audiences.', icon:Target, color:'#00D4FF' },
  { id:'grow_sov', name:'Grow Share of Voice', desc:'Increase brand visibility and market presence across all channels.', icon:Zap, color:'#FFD166' },
  { id:'protect_brand', name:'Brand Protection', desc:'Monitor and defend branded search terms from competitor hijacking.', icon:Shield, color:'#FF6B6B' },
];

export default function AgentSetupPage() {
  const { user } = useAuth();
  const { isDark, colors: T } = useTheme();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [agents, setAgents] = useState([]);
  const [config, setConfig] = useState({ name:'My OmniClaw Agent', goal:'', channels:[], budget_limit:0, auto_optimize:true, rules:{} });
  const [deploying, setDeploying] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/agents`, { withCredentials:true })
      .then(r => setAgents(r.data))
      .catch(e => console.error('Failed to load agents:', e));
  }, []); // API is module-level constant

  const channels = user?.connected_channels || [];
  const toggleChannel = (id) => {
    setConfig(c => ({ ...c, channels: c.channels.includes(id) ? c.channels.filter(x=>x!==id) : [...c.channels, id] }));
  };

  const createAndDeploy = async () => {
    setDeploying(true);
    try {
      const { data } = await axios.post(`${API}/api/agents/create`, config, { withCredentials:true });
      const agent = data.agent;
      setCreated(agent);
      await axios.post(`${API}/api/agents/${agent.agent_id}/deploy`, {}, { withCredentials:true });
      setStep(3);
      setTimeout(async () => {
        const res = await axios.get(`${API}/api/agents`, { withCredentials:true });
        setAgents(res.data);
      }, 4000);
    } catch (e) {
      alert('Error creating agent');
    }
    setDeploying(false);
  };

  const pauseAgent = async (agentId) => {
    await axios.post(`${API}/api/agents/${agentId}/pause`, {}, { withCredentials:true });
    const res = await axios.get(`${API}/api/agents`, { withCredentials:true });
    setAgents(res.data);
  };

  const stepTitles = ['Choose Goal','Select Channels','Configure & Deploy','Agent Live'];

  return (
    <div className={`min-h-screen py-8 px-4 ${isDark ? 'bg-zinc-950' : 'bg-zinc-50'}`} style={{ fontFamily:"'Outfit',sans-serif" }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <button data-testid="agent-back" onClick={() => navigate('/dashboard')}
            className={`flex items-center gap-2 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
            <ArrowLeft size={16}/> Dashboard
          </button>
          <div className="flex-1"/>
          <div className="flex items-center gap-2">
            <Bot size={20} style={{ color:'#06D6A0' }}/>
            <span className="text-lg font-bold">OmniClaw Agent Setup</span>
          </div>
        </div>

        {/* Existing agents */}
        {agents.length > 0 && step === 0 && (
          <div className={`mb-8 rounded-xl border p-6 ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-white'}`}>
            <h3 className="font-bold mb-4">Your Agents</h3>
            <div className="space-y-3">
              {agents.map(a => (
                <div key={a.agent_id} data-testid={`agent-${a.agent_id}`}
                  className={`flex items-center gap-4 p-4 rounded-lg border ${isDark ? 'border-zinc-700 bg-zinc-800' : 'border-zinc-200 bg-zinc-50'}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${a.status==='active'?'bg-green-500/20':'bg-zinc-500/20'}`}>
                    <Bot size={20} className={a.status==='active'?'text-green-500':'text-zinc-500'}/>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{a.name}</div>
                    <div className={`text-xs ${isDark?'text-zinc-500':'text-zinc-400'}`}>Goal: {a.goal} | Channels: {a.channels?.length || 0}</div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                    a.status==='active'?'bg-green-500/10 text-green-500':
                    a.status==='deploying'?'bg-blue-500/10 text-blue-500':
                    a.status==='paused'?'bg-orange-500/10 text-orange-500':
                    'bg-zinc-500/10 text-zinc-500'
                  }`}>{a.status}</div>
                  {a.status==='active' && (
                    <div className={`text-xs ${isDark?'text-zinc-400':'text-zinc-500'}`}>
                      {a.metrics?.actions_taken} actions | ROAS +{a.metrics?.roas_improvement}x
                    </div>
                  )}
                  {a.status==='active' && (
                    <button onClick={() => pauseAgent(a.agent_id)}
                      className="p-2 rounded-lg border border-orange-300 text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20">
                      <Pause size={14}/>
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button data-testid="create-new-agent" onClick={() => setStep(0)}
              className="mt-4 text-sm font-medium" style={{ color:'#00D4FF' }}>+ Create New Agent</button>
          </div>
        )}

        {/* Wizard */}
        <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-white shadow-lg'}`}>
          {/* Progress */}
          <div className={`px-6 py-4 border-b ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
            <div className="flex gap-2 mb-2">
              {stepTitles.map((_, i) => (
                <div key={i} className="flex-1 h-1.5 rounded-full transition-all"
                  style={{ background: i <= step ? '#06D6A0' : (isDark ? '#27272a' : '#e4e4e7') }}/>
              ))}
            </div>
            <div className="flex justify-between">
              <span className={`text-xs font-medium ${isDark?'text-zinc-400':'text-zinc-500'}`}>Step {step+1}: {stepTitles[step]}</span>
            </div>
          </div>

          <div className="p-6">
            {/* Step 0: Goal */}
            {step === 0 && (
              <div data-testid="agent-step-goal">
                <h2 className="text-xl font-bold mb-2">What should OmniClaw optimize?</h2>
                <p className={`text-sm mb-6 ${isDark?'text-zinc-400':'text-zinc-500'}`}>Choose a primary objective for your AI agent.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {GOALS.map(g => (
                    <button key={g.id} data-testid={`goal-${g.id}`} onClick={() => setConfig(c => ({...c, goal:g.id}))}
                      className={`text-left p-5 rounded-xl border transition-all ${config.goal===g.id ? 'ring-2' : ''} ${isDark ? 'border-zinc-700 bg-zinc-800 hover:border-zinc-500' : 'border-zinc-200 bg-white hover:border-zinc-400'}`}
                      style={config.goal===g.id ? { borderColor:g.color, boxShadow:`0 0 20px ${g.color}20` } : {}}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background:`${g.color}20` }}>
                          <g.icon size={18} style={{ color:g.color }}/>
                        </div>
                        <span className="font-bold">{g.name}</span>
                        {config.goal===g.id && <Check size={16} style={{ color:g.color }}/>}
                      </div>
                      <p className={`text-sm ${isDark?'text-zinc-400':'text-zinc-500'}`}>{g.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 1: Channels */}
            {step === 1 && (
              <div data-testid="agent-step-channels">
                <h2 className="text-xl font-bold mb-2">Which channels should the agent manage?</h2>
                <p className={`text-sm mb-6 ${isDark?'text-zinc-400':'text-zinc-500'}`}>
                  Select from your connected channels. {config.channels.length} selected.
                </p>
                {channels.length === 0 ? (
                  <div className={`text-center py-8 rounded-lg border ${isDark?'border-zinc-700 bg-zinc-800':'border-zinc-200 bg-zinc-50'}`}>
                    <p className={`text-sm ${isDark?'text-zinc-400':'text-zinc-500'}`}>No channels connected yet.</p>
                    <button onClick={() => navigate('/onboarding')} className="mt-2 text-sm font-medium" style={{color:'#00D4FF'}}>Connect channels first</button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {channels.map(ch => {
                      const sel = config.channels.includes(ch.channel_id);
                      return (
                        <button key={ch.channel_id} data-testid={`agent-ch-${ch.channel_id}`} onClick={() => toggleChannel(ch.channel_id)}
                          className={`flex items-center gap-2.5 p-3 rounded-lg border text-left transition-all text-sm ${
                            sel ? (isDark?'border-green-700 bg-green-900/20':'border-green-400 bg-green-50') :
                            (isDark?'border-zinc-700 bg-zinc-800':'border-zinc-200 bg-white')}`}>
                          <span className="font-medium flex-1">{ch.channel_name}</span>
                          {sel && <Check size={14} className="text-green-500"/>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Config */}
            {step === 2 && (
              <div data-testid="agent-step-config">
                <h2 className="text-xl font-bold mb-2">Configure your agent</h2>
                <p className={`text-sm mb-6 ${isDark?'text-zinc-400':'text-zinc-500'}`}>Set a name, budget limits, and optimization rules.</p>
                <div className="space-y-4 max-w-md">
                  <div>
                    <label className={`text-sm font-medium block mb-1 ${isDark?'text-zinc-300':'text-zinc-700'}`}>Agent Name</label>
                    <input data-testid="agent-name-input" value={config.name} onChange={e => setConfig(c=>({...c, name:e.target.value}))}
                      className={`w-full px-4 py-2.5 rounded-lg text-sm border outline-none ${isDark?'bg-zinc-800 border-zinc-700 text-white':'bg-white border-zinc-300 text-zinc-900'}`}/>
                  </div>
                  <div>
                    <label className={`text-sm font-medium block mb-1 ${isDark?'text-zinc-300':'text-zinc-700'}`}>Monthly Budget Limit ($)</label>
                    <input data-testid="agent-budget-input" type="number" value={config.budget_limit}
                      onChange={e => setConfig(c=>({...c, budget_limit:parseFloat(e.target.value)||0}))}
                      className={`w-full px-4 py-2.5 rounded-lg text-sm border outline-none ${isDark?'bg-zinc-800 border-zinc-700 text-white':'bg-white border-zinc-300 text-zinc-900'}`}/>
                  </div>
                  <div className="flex items-center gap-3">
                    <button data-testid="agent-auto-toggle" onClick={() => setConfig(c=>({...c, auto_optimize:!c.auto_optimize}))}
                      className={`w-10 h-6 rounded-full transition-all ${config.auto_optimize?'bg-green-500':'bg-zinc-400'}`}>
                      <div className={`w-4 h-4 rounded-full bg-white transition-all ${config.auto_optimize?'translate-x-5':'translate-x-1'}`}/>
                    </button>
                    <span className={`text-sm font-medium ${isDark?'text-zinc-300':'text-zinc-700'}`}>Auto-optimize bids & budgets</span>
                  </div>
                  <div className={`p-4 rounded-lg border ${isDark?'border-zinc-700 bg-zinc-800':'border-zinc-200 bg-zinc-50'}`}>
                    <div className="text-sm font-bold mb-2">Agent Summary</div>
                    <div className={`text-xs space-y-1 ${isDark?'text-zinc-400':'text-zinc-500'}`}>
                      <p>Goal: <strong>{GOALS.find(g=>g.id===config.goal)?.name || 'Not set'}</strong></p>
                      <p>Channels: <strong>{config.channels.length}</strong></p>
                      <p>Budget Limit: <strong>${config.budget_limit}/mo</strong></p>
                      <p>Auto-optimize: <strong>{config.auto_optimize?'Yes':'No'}</strong></p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Deployed */}
            {step === 3 && (
              <div data-testid="agent-step-deployed" className="text-center py-8">
                <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background:'#06D6A0' }}>
                  <Bot size={28} className="text-white"/>
                </div>
                <h2 className="text-2xl font-bold mb-2">Agent Deployed!</h2>
                <p className={`text-sm mb-2 ${isDark?'text-zinc-400':'text-zinc-500'}`}>
                  <strong>{config.name}</strong> is now active and monitoring your channels.
                </p>
                <p className={`text-xs ${isDark?'text-zinc-600':'text-zinc-400'}`}>
                  It will start optimizing within the next few minutes. Check back for performance metrics.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={`px-6 py-4 border-t flex justify-between ${isDark?'border-zinc-800':'border-zinc-200'}`}>
            {step > 0 && step < 3 ? (
              <button data-testid="agent-prev" onClick={() => setStep(step-1)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${isDark?'text-zinc-400':'text-zinc-500'}`}>
                <ArrowLeft size={16}/> Back
              </button>
            ) : <div/>}
            {step < 2 && (
              <button data-testid="agent-next" onClick={() => setStep(step+1)} disabled={step===0 && !config.goal}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ background:'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
                Next <ArrowRight size={16}/>
              </button>
            )}
            {step === 2 && (
              <button data-testid="agent-deploy" onClick={createAndDeploy} disabled={deploying}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold text-white disabled:opacity-50"
                style={{ background:'#06D6A0' }}>
                {deploying ? <Loader2 size={16} className="animate-spin"/> : <Play size={16}/>}
                Deploy Agent
              </button>
            )}
            {step === 3 && (
              <button data-testid="agent-go-dashboard" onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white"
                style={{ background:'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
                Go to Dashboard <ArrowRight size={16}/>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
